import React, { useState, useEffect } from 'react';
import { useChat } from '../../../hooks/useChat';
import { Sparkles, RefreshCw, ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Leaf } from 'lucide-react';
import toast from 'react-hot-toast';

const AITreatmentSuggestions = ({ weatherData, scoutingLogs, tasks }) => {
  const [suggestions, setSuggestions] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [hasGenerated, setHasGenerated] = useState(false);

  const { response, isLoading, error, sendMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    false
  );

  useEffect(() => {
    if (error) toast?.error('AI suggestions unavailable. Please try again.');
  }, [error]);

  useEffect(() => {
    if (response && !isLoading) {
      try {
        const jsonMatch = response?.match(/```json\n?([\s\S]*?)\n?```/) || response?.match(/(\{[\s\S]*\})/);
        if (jsonMatch) {
          setSuggestions(JSON.parse(jsonMatch?.[1]));
        } else {
          setSuggestions({ raw: response });
        }
      } catch {
        setSuggestions({ raw: response });
      }
    }
  }, [response, isLoading]);

  const generateSuggestions = () => {
    const weather = weatherData || {
      temperature: 24,
      humidity: 65,
      windSpeed: 8,
      condition: 'Partly Cloudy',
      uvIndex: 6
    };

    const pestActivity = scoutingLogs?.slice(0, 5)?.map(log =>
      `${log?.pestType || 'Unknown pest'} (${log?.severity || 'low'} severity) at ${log?.fieldName || 'field'}`
    )?.join('; ') || 'No recent scouting logs available';

    const activeTasks = tasks?.filter(t => t?.status !== 'completed')?.slice(0, 5)?.map(t =>
      `${t?.title || t?.name || 'Task'} (${t?.status || 'pending'})`
    )?.join('; ') || 'No active tasks';

    const messages = [
      {
        role: 'system',
        content: `You are an expert IPM (Integrated Pest Management) agronomist. 
Analyze pest activity and weather conditions to provide actionable treatment recommendations.
Always respond with valid JSON in this exact format:
{
  "urgency": "high|medium|low",
  "summary": "Brief 1-sentence overview",
  "recommendations": [
    {
      "priority": "high|medium|low",
      "action": "Treatment action title",
      "detail": "Specific instructions",
      "timing": "When to apply (e.g., Early morning, Avoid midday heat)",
      "rationale": "Why this is recommended based on conditions"
    }
  ],
  "weatherNote": "Key weather consideration affecting treatments",
  "ipmTip": "One IPM best practice tip"
}`
      },
      {
        role: 'user',
        content: `Current conditions for IPM treatment planning:

WEATHER:
- Temperature: ${weather?.temperature}°C
- Humidity: ${weather?.humidity}%
- Wind Speed: ${weather?.windSpeed} km/h
- Condition: ${weather?.condition}
- UV Index: ${weather?.uvIndex}

PEST ACTIVITY (recent scouting):
${pestActivity}

ACTIVE OPERATIONS:
${activeTasks}

Provide 3-4 prioritized treatment recommendations based on these conditions.`
      }
    ];

    setHasGenerated(true);
    setSuggestions(null);
    sendMessage(messages, { temperature: 0.4, max_tokens: 800 });
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getUrgencyIcon = (urgency) => {
    switch (urgency?.toLowerCase()) {
      case 'high': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'medium': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default: return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-green-100">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-green-100 rounded-lg">
            <Sparkles className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">AI Treatment Suggestions</h3>
            <p className="text-xs text-gray-500">Powered by pest data &amp; weather analysis</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={generateSuggestions}
            disabled={isLoading}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            <span>{isLoading ? 'Analyzing...' : hasGenerated ? 'Refresh' : 'Analyze'}</span>
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="p-4">
          {/* Initial state */}
          {!hasGenerated && !isLoading && (
            <div className="text-center py-6">
              <Leaf className="w-10 h-10 text-green-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-3">
                Get AI-powered treatment recommendations based on current pest activity and weather conditions.
              </p>
              <button
                onClick={generateSuggestions}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Generate Suggestions
              </button>
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="space-y-4 animate-pulse">
              {/* Summary banner skeleton */}
              <div className="flex items-center space-x-2 p-3 bg-gray-100 rounded-lg border border-gray-200">
                <div className="w-4 h-4 bg-gray-300 rounded-full flex-shrink-0"></div>
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-gray-300 rounded w-4/5"></div>
                  <div className="h-3 bg-gray-200 rounded w-3/5"></div>
                </div>
              </div>

              {/* Recommendation card skeletons */}
              {[1, 2, 3]?.map(i => (
                <div key={i} className="p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="h-3 bg-gray-300 rounded w-1/3"></div>
                    <div className="h-5 bg-gray-200 rounded-full w-14"></div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-3 bg-gray-100 rounded w-2/5"></div>
                  <div className="h-3 bg-gray-100 rounded w-3/4 italic"></div>
                </div>
              ))}

              {/* Weather note skeleton */}
              <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="w-4 h-4 bg-blue-200 rounded flex-shrink-0 mt-0.5"></div>
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-blue-200 rounded w-full"></div>
                  <div className="h-3 bg-blue-100 rounded w-3/4"></div>
                </div>
              </div>

              {/* IPM tip skeleton */}
              <div className="flex items-start space-x-2 p-3 bg-purple-50 rounded-lg border border-purple-100">
                <div className="w-3 h-3 bg-purple-200 rounded flex-shrink-0 mt-0.5"></div>
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-purple-200 rounded w-1/4 mb-1"></div>
                  <div className="h-3 bg-purple-100 rounded w-full"></div>
                  <div className="h-3 bg-purple-100 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {suggestions && !isLoading && (
            <div className="space-y-4">
              {/* Summary banner */}
              {suggestions?.summary && (
                <div className="flex items-start space-x-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  {getUrgencyIcon(suggestions?.urgency)}
                  <p className="text-sm text-gray-700 font-medium">{suggestions?.summary}</p>
                </div>
              )}

              {/* Recommendations */}
              {suggestions?.recommendations?.map((rec, idx) => (
                <div key={idx} className={`p-3 rounded-lg border ${getPriorityColor(rec?.priority)}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wide">{rec?.action}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${getPriorityColor(rec?.priority)}`}>
                      {rec?.priority}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 mb-1">{rec?.detail}</p>
                  {rec?.timing && (
                    <p className="text-xs text-gray-500">⏰ {rec?.timing}</p>
                  )}
                  {rec?.rationale && (
                    <p className="text-xs text-gray-500 mt-1 italic">{rec?.rationale}</p>
                  )}
                </div>
              ))}

              {/* Raw response fallback */}
              {suggestions?.raw && (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">{suggestions?.raw}</p>
                </div>
              )}

              {/* Weather note */}
              {suggestions?.weatherNote && (
                <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-blue-500 text-xs">🌤</span>
                  <p className="text-xs text-blue-700">{suggestions?.weatherNote}</p>
                </div>
              )}

              {/* IPM tip */}
              {suggestions?.ipmTip && (
                <div className="flex items-start space-x-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <Leaf className="w-3 h-3 text-purple-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-purple-700"><span className="font-medium">IPM Tip:</span> {suggestions?.ipmTip}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AITreatmentSuggestions;
